import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PhoneIcon from "@mui/icons-material/Phone";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { RoutePaths } from "../../../../enumStrings";
import {
  CmpOperationalSourceKindEnum,
  CmpPaymentChoiceTypeEnum,
  useCreateCmpInvoiceMutation,
  useDownloadCmpInvoicePdfMutation,
  useGetCmpAdvertisingOptionsQuery,
  useGetChamberMemberProSettingsQuery,
  useGetCmpInvoiceCreateLookupsQuery,
  useSendCmpInvoicePaymentRequestMutation,
} from "../../../../graphql/types";
import useGetChamberFromUserIdInAuth from "../../../../hooks/useGetChamberFromUserIdInAuth";
import { getCmpCreditCardPaymentsReady } from "../../../../utils/cmpCreditCardPayments";
import { usePageHeaderOverride } from "../../../Layout/PageContainer";
import { FormPageShell } from "../../../common/dashboard/FormPageShell";
import { ModalShell } from "../../../common/dashboard/ModalShell";
import { cmpLongDateFormatter } from "../../../common/dashboard/formatters";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import { dateInputToUtcMilli, toBackendLineItems } from "../invoiceBackendAdapters";
import { getInvoiceDueDateInput } from "../invoiceDueDate";
import { downloadBase64File } from "../invoiceFileDownloads";
import { formatCurrency } from "../invoiceFormatting";
import { CMP_INVOICE_CREATE_LOOKUP_LIMIT } from "../invoiceLookupConfig";
import { cpaGold, cpaGoldDark, InvoiceSection } from "../InvoiceMockComponents";
import {
  getInvoiceSubtotalInCents,
  getInvoiceTaxInCents,
  getInvoiceTotalInCents,
  MockInvoiceLineItem,
  todayIso,
} from "../mockInvoiceData";
import { AdvertisingOptionsManager } from "./AdvertisingOptionsManager";
import {
  createEmptyLineItem,
  formatTaxSummaryLabel,
  getCategoryDefaults,
  getLineItemTaxOptions,
  InvoiceTab,
  isPristineEmptyLineItem,
  isValidInvoiceEmail,
} from "./createInvoiceHelpers";
import {
  InvoiceLineItemsTable,
  LineItemFieldErrors,
} from "./InvoiceLineItemsTable";

const shouldMarkCmpInvoiceModuleTestData = process.env.NODE_ENV === "development";

// POL-019 (B5): per-field validation errors, Members FormErrors pattern
// (CreateMember.tsx). Line-item errors use `lineItems.{id}.{field}` keys.
type FormErrors = Record<string, string>;

// Scroll the first field carrying a validation error into view; fields with
// errors mark their wrapper with data-invoice-field-error="true" in DOM order.
const scrollToFirstFieldError = () => {
  window.setTimeout(() => {
    document
      .querySelector('[data-invoice-field-error="true"]')
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 60);
};

const TABS: Array<{
  id: InvoiceTab;
  label: string;
  Icon: typeof CampaignOutlinedIcon;
}> = [
  { id: "advertising", label: "Advertising", Icon: CampaignOutlinedIcon },
  // TICK-2 (B5): mock shows a gear glyph for the Custom tab; the Advertising
  // glyph stays CampaignOutlined per operator decision (Q-035 family).
  { id: "custom", label: "Custom", Icon: SettingsOutlinedIcon },
];

const headerActionSx = {
  backgroundColor: cpaGold,
  borderRadius: "6px",
  color: "#fff",
  fontSize: 16,
  fontWeight: 800,
  minHeight: 52,
  minWidth: 172,
  px: 2.75,
  textTransform: "none" as const,
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon svg": { fontSize: 22 },
  "&:hover": { backgroundColor: cpaGoldDark },
};

const headerDecisionActionWidth = 198;

const headerDecisionActionSx = {
  ...headerActionSx,
  minWidth: headerDecisionActionWidth,
  width: "100%",
};

const invoiceFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    minHeight: 56,
  },
  "& .MuiInputBase-input": {
    fontSize: 15,
  },
  "& .MuiInputLabel-root": {
    fontSize: 15,
    fontWeight: 700,
  },
};

// Format a date-input value (yyyy-mm-dd) for display without the local-timezone
// day shift you get from `new Date("yyyy-mm-dd")` (which parses as UTC midnight).
const formatInvoiceDateForPreview = (dateInput: string): string => {
  if (!dateInput) {
    return "";
  }

  const parsed = new Date(`${dateInput}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return dateInput;
  }

  return cmpLongDateFormatter.format(parsed);
};

type InvoiceEmailPreviewProps = {
  billingContactName: string;
  billingEmail: string;
  billingInquiryEmail?: string | null;
  billingPhone?: string | null;
  chamberName: string;
  customerName: string;
  dueDateInput: string;
  issueDateInput: string;
  lineItems: MockInvoiceLineItem[];
  paymentTermsNote?: string | null;
  referenceNumber: string;
  subtotalInCents: number;
  taxInCents: number;
  taxRateLabel: string;
  taxRegistrationNumber?: string | null;
  totalInCents: number;
};

// Client-side faithful render of the invoice payment-request email. No email
// preview GraphQL op exists (INV-CREATE-003), so this composes the same content
// the backend payment-request email carries from the data already on the form:
// billing contact, chamber name/HST, payment-terms note, and the invoice being
// built. Keep this aligned with the sent email body if the backend template
// changes.
const InvoiceEmailPreview = ({
  billingContactName,
  billingEmail,
  billingInquiryEmail,
  billingPhone,
  chamberName,
  customerName,
  dueDateInput,
  issueDateInput,
  lineItems,
  paymentTermsNote,
  referenceNumber,
  subtotalInCents,
  taxInCents,
  taxRateLabel,
  taxRegistrationNumber,
  totalInCents,
}: InvoiceEmailPreviewProps) => {
  const greetingName = billingContactName.trim() || customerName.trim() || "there";
  const issueDateLabel = formatInvoiceDateForPreview(issueDateInput);
  const dueDateLabel = formatInvoiceDateForPreview(dueDateInput);
  const renderableLineItems = lineItems.filter(
    (item) => item.description.trim() || item.quantity > 0 || item.unitPriceInCents > 0
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-6 py-4 text-sm text-gray-600">
        <div>
          <span className="font-bold text-gray-900">To:</span>{" "}
          {billingEmail || "the billing contact"}
        </div>
        <div className="mt-1">
          <span className="font-bold text-gray-900">Subject:</span> Invoice from{" "}
          {chamberName}
        </div>
      </div>

      <div className="space-y-4 px-6 py-6 text-sm leading-6 text-gray-700">
        <p>Hello {greetingName},</p>
        <p>
          Thank you for your continued support of {chamberName}. Please find your
          invoice summary below. You can pay online, by e-transfer, cheque, direct
          deposit, or cash.
        </p>

        <div className="rounded-md border border-gray-200">
          <div className="grid grid-cols-2 gap-2 border-b border-gray-100 px-4 py-3 text-[13px]">
            <span className="text-gray-500">Billed To</span>
            <span className="text-right font-semibold text-gray-900">
              {customerName.trim() || "—"}
            </span>
            <span className="text-gray-500">Invoice Date</span>
            <span className="text-right font-semibold text-gray-900">
              {issueDateLabel || "—"}
            </span>
            <span className="text-gray-500">Due Date</span>
            <span className="text-right font-semibold text-gray-900">
              {dueDateLabel || "—"}
            </span>
            {referenceNumber.trim() ? (
              <>
                <span className="text-gray-500">Reference / PO</span>
                <span className="text-right font-semibold text-gray-900">
                  {referenceNumber.trim()}
                </span>
              </>
            ) : null}
          </div>

          <table className="w-full border-collapse text-left text-[13px]">
            <thead>
              <tr className="bg-gray-50 text-[11px] font-bold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2 text-center">Qty</th>
                <th className="px-4 py-2 text-right">Unit Price</th>
                <th className="px-4 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {renderableLineItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-3 text-center text-gray-400"
                  >
                    No line items added yet.
                  </td>
                </tr>
              ) : (
                renderableLineItems.map((item) => (
                  <tr key={item.id} className="border-t border-gray-100">
                    <td className="px-4 py-2 text-gray-900">
                      {item.description.trim() || item.code || "Invoice item"}
                    </td>
                    <td className="px-4 py-2 text-center text-gray-700">
                      {item.quantity}
                    </td>
                    <td className="px-4 py-2 text-right text-gray-700">
                      {formatCurrency(item.unitPriceInCents)}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-gray-900">
                      {formatCurrency(item.quantity * item.unitPriceInCents)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className="space-y-1 border-t border-gray-100 px-4 py-3 text-[13px]">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(subtotalInCents)}
              </span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>{taxRateLabel}</span>
              <span className="font-semibold text-gray-900">
                {formatCurrency(taxInCents)}
              </span>
            </div>
            <div className="flex justify-between pt-1 text-base font-extrabold text-gray-900">
              <span>Total Due</span>
              <span>{formatCurrency(totalInCents)}</span>
            </div>
          </div>
        </div>

        {paymentTermsNote ? (
          <p className="text-gray-600">{paymentTermsNote}</p>
        ) : (
          <p className="text-gray-600">
            Thank you for your support of {chamberName}.
          </p>
        )}

        {billingInquiryEmail || billingPhone ? (
          <p className="text-gray-600">
            Billing inquiries:{" "}
            {billingInquiryEmail ? (
              <span className="font-semibold text-gray-900">
                {billingInquiryEmail}
              </span>
            ) : null}
            {billingInquiryEmail && billingPhone ? " · " : ""}
            {billingPhone ? (
              <span className="font-semibold text-gray-900">{billingPhone}</span>
            ) : null}
          </p>
        ) : null}

        <div className="border-t border-gray-100 pt-4 text-center text-[13px] font-semibold text-gray-900">
          {chamberName} HST#
          {taxRegistrationNumber ? `: ${taxRegistrationNumber}` : ""}
        </div>

        {/* TICK-6 (B5): centered closing line below the HST# line per mock. */}
        <div className="text-center text-[13px] text-gray-700">
          Thank you for being a valued member of the {chamberName}.
        </div>
      </div>
    </div>
  );
};

const CreateInvoice = () => {
  const navigate = useNavigate();
  const setPageHeaderOverride = usePageHeaderOverride();
  const chamber = useGetChamberFromUserIdInAuth();
  const chamberId = chamber?.id || "";
  const { data: chamberSettingsData } = useGetChamberMemberProSettingsQuery({
    variables: { chamberId },
    skip: !chamberId,
    fetchPolicy: "cache-first",
  });

  const [activeTab, setActiveTab] = useState<InvoiceTab>("advertising");
  // INV-BLAIR-001: invoices can target an existing member or a one-off,
  // non-member customer. The backend chamberMemberId is optional; in
  // non-member mode we omit it and require an editable customer name instead.
  const [customerKind, setCustomerKind] = useState<"member" | "non-member">(
    "member"
  );
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [issueDate, setIssueDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState(todayIso());
  const [dueDateManuallyEdited, setDueDateManuallyEdited] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [lineItems, setLineItems] = useState<MockInvoiceLineItem[]>([
    createEmptyLineItem({}),
  ]);
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [pendingTabSwitch, setPendingTabSwitch] = useState<InvoiceTab | null>(null);
  const seededInitialLineDefaults = useRef(false);

  const {
    data: lookupData,
    loading: lookupsLoading,
    error: lookupsError,
  } = useGetCmpInvoiceCreateLookupsQuery({
    variables: {
      input: { chamberId, limit: CMP_INVOICE_CREATE_LOOKUP_LIMIT },
    },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const {
    data: advertisingData,
    loading: advertisingLoading,
    refetch: refetchAdvertisingOptions,
  } = useGetCmpAdvertisingOptionsQuery({
    variables: { input: { chamberId } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
  });
  const [createCmpInvoice, { loading: creatingInvoice }] =
    useCreateCmpInvoiceMutation();
  const [downloadCmpInvoicePdf, { loading: downloadingPdf }] =
    useDownloadCmpInvoicePdfMutation();
  const [sendCmpInvoicePaymentRequest, { loading: sendingInvoice }] =
    useSendCmpInvoicePaymentRequestMutation();

  const members = useMemo(
    () => lookupData?.getCmpInvoiceCreateLookups.members || [],
    [lookupData]
  );
  const invoiceDefaults =
    lookupData?.getCmpInvoiceCreateLookups.invoiceDefaults || null;
  const invoicePaymentTerms =
    chamberSettingsData?.getChamberMemberProSettings.paymentsAndTaxes
      .invoicePaymentTerms;
  const stripeConnect =
    chamberSettingsData?.getChamberMemberProSettings.paymentsAndTaxes
      .stripeConnect;
  const creditCardPaymentsReady = getCmpCreditCardPaymentsReady(stripeConnect);
  const advertisingOptions = useMemo(
    () => advertisingData?.getCmpAdvertisingOptions.advertisingOptions || [],
    [advertisingData]
  );
  const lineItemCatalogOptions = useMemo(
    () => (activeTab === "advertising" ? advertisingOptions : []),
    [activeTab, advertisingOptions]
  );

  const categoryDefaults = getCategoryDefaults(invoiceDefaults, activeTab);
  const accountNumber = categoryDefaults?.accountNumber || "";
  const taxOptions = getLineItemTaxOptions(categoryDefaults);
  const defaultTaxRate =
    taxOptions.find((option) => option.ratePercentage > 0)?.ratePercentage || 0;

  const subtotalInCents = getInvoiceSubtotalInCents({ lineItems });
  const taxInCents = getInvoiceTaxInCents({ lineItems });
  const totalInCents = getInvoiceTotalInCents({ lineItems });
  const submitting = creatingInvoice || sendingInvoice || downloadingPdf;

  useEffect(() => {
    if (!invoicePaymentTerms || dueDateManuallyEdited) {
      return;
    }

    setDueDate(
      getInvoiceDueDateInput({ invoicePaymentTerms, issueDate })
    );
  }, [dueDateManuallyEdited, invoicePaymentTerms, issueDate]);

  useEffect(() => {
    if (!categoryDefaults || seededInitialLineDefaults.current) {
      return;
    }

    setLineItems((items) => {
      if (items.length !== 1 || !isPristineEmptyLineItem(items[0])) {
        seededInitialLineDefaults.current = true;
        return items;
      }

      seededInitialLineDefaults.current = true;
      return [
        {
          ...items[0],
          accountNumber,
          taxRate: defaultTaxRate,
        },
      ];
    });
  }, [accountNumber, categoryDefaults, defaultTaxRate]);

  const getFieldError = (key: string) => formErrors[key] || "";

  // POL-019 (B5): a field's error clears as soon as the field changes.
  const clearFieldError = (...keys: string[]) =>
    setFormErrors((previous) => {
      const staleKeys = keys.filter((key) => previous[key]);

      if (staleKeys.length === 0) {
        return previous;
      }

      const next = { ...previous };
      staleKeys.forEach((key) => delete next[key]);
      return next;
    });

  const handleSelectMember = (memberId: string) => {
    setSelectedMemberId(memberId);
    setIsDirty(true);
    // Selecting a member fills the billing snapshot fields below, so their
    // stale required-field errors clear along with the member error.
    clearFieldError(
      "memberId",
      "customerName",
      "street",
      "city",
      "postalCode",
      "contactName",
      "contactEmail"
    );

    const option = members.find((candidate) => candidate.id === memberId);
    if (!option) {
      return;
    }

    setCustomerName(option.billingSnapshot.customerName);
    setContactName(option.billingSnapshot.contactName || "");
    setContactEmail(option.billingSnapshot.contactEmail);
    setContactPhone(option.billingSnapshot.contactPhone || "");
    setStreet(option.billingSnapshot.addressLine1 || "");
    setCity(option.billingSnapshot.city || "");
    setProvince(option.billingSnapshot.province || "");
    setPostalCode(option.billingSnapshot.postalCode || "");
  };

  // INV-BLAIR-001: switching to a non-member customer clears the member
  // selection (so we never send a stale chamberMemberId) but keeps the
  // already-entered billing fields, which stay editable in both modes.
  const handleChangeCustomerKind = (kind: "member" | "non-member") => {
    if (kind === customerKind) {
      return;
    }

    setCustomerKind(kind);
    setIsDirty(true);
    if (kind === "non-member") {
      setSelectedMemberId("");
    }
  };

  const handleLineItemsChange = (nextLineItems: MockInvoiceLineItem[]) => {
    // POL-019 (B5): clear line errors for removed lines and for line fields
    // that changed. The unit-price constraint is collective (at least one
    // line > $0.00), so any price edit clears all unit-price errors until the
    // next submit re-evaluates.
    setFormErrors((previous) => {
      const lineKeys = Object.keys(previous).filter((key) =>
        key.startsWith("lineItems.")
      );

      if (lineKeys.length === 0) {
        return previous;
      }

      const previousById = new Map(lineItems.map((item) => [item.id, item]));
      const nextById = new Map(nextLineItems.map((item) => [item.id, item]));
      const anyPriceChanged = nextLineItems.some((item) => {
        const previousItem = previousById.get(item.id);
        return previousItem
          ? previousItem.unitPriceInCents !== item.unitPriceInCents
          : false;
      });
      const next = { ...previous };
      let changed = false;

      lineKeys.forEach((key) => {
        const match = key.match(/^lineItems\.(.+)\.(description|unitPrice)$/);

        if (!match) {
          return;
        }

        const [, lineItemId, field] = match;
        const nextItem = nextById.get(lineItemId);

        if (!nextItem) {
          delete next[key];
          changed = true;
          return;
        }

        if (field === "unitPrice" && anyPriceChanged) {
          delete next[key];
          changed = true;
          return;
        }

        const previousItem = previousById.get(lineItemId);
        if (
          field === "description" &&
          previousItem &&
          (previousItem.description !== nextItem.description ||
            previousItem.catalogOptionId !== nextItem.catalogOptionId)
        ) {
          delete next[key];
          changed = true;
        }
      });

      return changed ? next : previous;
    });
    setLineItems(nextLineItems);
    setIsDirty(true);
  };

  // Switching tabs changes the tax category; remap any line whose tax rate is no
  // longer a valid option for the new tab to that tab's default rate.
  const applyTabSwitch = (tab: InvoiceTab) => {
    setActiveTab(tab);
    const nextOptions = getLineItemTaxOptions(getCategoryDefaults(invoiceDefaults, tab));
    const validRates = new Set(nextOptions.map((option) => option.ratePercentage));
    const nextDefaultRate =
      nextOptions.find((option) => option.ratePercentage > 0)?.ratePercentage || 0;
    setLineItems((items) =>
      items.map((item) =>
        validRates.has(item.taxRate) ? item : { ...item, taxRate: nextDefaultRate }
      )
    );
  };

  // Switching tabs after edits would silently discard in-progress invoice work,
  // so confirm first when the form is dirty (INV-ADV-002). Reuses the CMP
  // unsaved-changes copy/styling instead of inventing tab-specific warning text.
  const handleChangeTab = (tab: InvoiceTab) => {
    if (tab === activeTab) {
      return;
    }

    if (isDirty) {
      setPendingTabSwitch(tab);
      return;
    }

    applyTabSwitch(tab);
  };

  const handleConfirmTabSwitch = () => {
    if (!pendingTabSwitch) {
      return;
    }

    applyTabSwitch(pendingTabSwitch);
    setPendingTabSwitch(null);
  };

  const submitInvoice = useCallback(async ({
    createAsDraft,
    downloadPdf = false,
    navigateAfterSave = true,
    send,
  }: {
    createAsDraft: boolean;
    downloadPdf?: boolean;
    navigateAfterSave?: boolean;
    send: boolean;
  }) => {
    const isMemberInvoice = customerKind === "member";
    const selectedMember = members.find((member) => member.id === selectedMemberId);

    // POL-019 (B5): per-field validation, Members FormErrors pattern. The top
    // Alert stays as the summary — this page's action buttons live in the
    // page header, so the Alert already sits near the action.
    // Drafts (Print PDF) keep the legacy required set (identity + billing
    // email + total) so partial drafts stay saveable; the full asterisked
    // set gates only the Approve paths.
    const nextErrors: FormErrors = {};

    if (isMemberInvoice && !selectedMember) {
      nextErrors.memberId = "Select a member for this invoice first.";
    }

    if (!isMemberInvoice && !customerName.trim()) {
      nextErrors.customerName = "Customer or company name is required.";
    }

    if (!createAsDraft) {
      if (!street.trim()) {
        nextErrors.street = "Street number and name are required.";
      }

      if (!city.trim()) {
        nextErrors.city = "City is required.";
      }

      if (!postalCode.trim()) {
        nextErrors.postalCode = "Postal code is required.";
      }

      if (!contactName.trim()) {
        nextErrors.contactName = "Billing contact name is required.";
      }
    }

    if (!contactEmail.trim()) {
      nextErrors.contactEmail = "Billing email is required.";
    } else if (!isValidInvoiceEmail(contactEmail)) {
      nextErrors.contactEmail = "Enter a valid billing email address.";
    }

    if (!createAsDraft) {
      if (!issueDate) {
        nextErrors.issueDate = "Invoice date is required.";
      }

      if (!dueDate) {
        nextErrors.dueDate = "Due date is required.";
      }

      lineItems.forEach((item) => {
        if (!item.description.trim()) {
          nextErrors[`lineItems.${item.id}.description`] =
            "Select or enter a description for this line.";
        }
      });
    }

    if (totalInCents <= 0) {
      lineItems.forEach((item) => {
        if (item.unitPriceInCents <= 0) {
          nextErrors[`lineItems.${item.id}.unitPrice`] =
            "Enter a unit price greater than $0.00.";
        }
      });
    }

    setFormErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setErrorMessage(
        totalInCents <= 0
          ? "Invoice total must be greater than $0.00. Fix the highlighted fields below and try again."
          : "Fix the highlighted fields below and try again."
      );
      scrollToFirstFieldError();
      return;
    }

    try {
      setErrorMessage(null);

      if (!chamberId) {
        throw new Error("Chamber is still loading. Try again in a moment.");
      }

      const internalNotes = [
        notes.trim(),
        referenceNumber.trim() ? `PO #: ${referenceNumber.trim()}` : "",
      ]
        .filter(Boolean)
        .join("\n");

      const result = await createCmpInvoice({
        variables: {
          input: {
            chamberId,
            sourceKind:
              activeTab === "advertising"
                ? CmpOperationalSourceKindEnum.Advertising
                : CmpOperationalSourceKindEnum.CustomInvoice,
            chamberMemberId: isMemberInvoice
              ? selectedMember?.chamberMemberId || undefined
              : undefined,
            billingSnapshot: {
              customerName: customerName.trim(),
              contactName: contactName.trim() || undefined,
              contactEmail: contactEmail.trim(),
              contactPhone: contactPhone.trim() || undefined,
              addressLine1: street.trim() || undefined,
              city: city.trim() || undefined,
              province: province.trim() || undefined,
              postalCode: postalCode.trim() || undefined,
              country: "Canada",
            },
            lineItems: toBackendLineItems(lineItems),
            paymentChoice: {
              type: creditCardPaymentsReady
                ? CmpPaymentChoiceTypeEnum.CreditCard
                : CmpPaymentChoiceTypeEnum.Manual,
              autoRenew: false,
              emailCommunicationConsentConfirmed: true,
            },
            issuedAtUtcMilli: dateInputToUtcMilli(issueDate),
            dueAtUtcMilli: dateInputToUtcMilli(dueDate),
            internalNotes: internalNotes || undefined,
            createAsDraft,
            isCmpInvoiceModuleTestData:
              shouldMarkCmpInvoiceModuleTestData || undefined,
          },
        },
      });

      const created = result.data?.createCmpInvoice;
      const invoiceId = created?.invoice?.id;

      if (!created?.invoiceSavedSuccessfully || !invoiceId) {
        throw new Error("Invoice could not be saved.");
      }

      if (send) {
        const sendResult = await sendCmpInvoicePaymentRequest({
          variables: {
            input: {
              chamberId,
              invoiceId,
              isCmpInvoiceModuleTestData:
                shouldMarkCmpInvoiceModuleTestData || undefined,
            },
          },
        });

        if (
          !sendResult.data?.sendCmpInvoicePaymentRequest.actionCompletedSuccessfully
        ) {
          throw new Error(
            "Invoice was saved, but the email could not be sent. Resend it from Manage Invoices."
          );
        }
      }

      if (downloadPdf) {
        const pdfResult = await downloadCmpInvoicePdf({
          variables: {
            input: {
              chamberId,
              invoiceId,
              isCmpInvoiceModuleTestData:
                shouldMarkCmpInvoiceModuleTestData || undefined,
            },
          },
        });
        const download = pdfResult.data?.downloadCmpInvoicePdf.download;

        if (!download) {
          throw new Error(
            "Invoice was saved as a draft, but the PDF could not be generated."
          );
        }

        downloadBase64File(download);
      }

      setIsDirty(false);
      setLastAction(
        downloadPdf
          ? "Draft invoice saved and PDF downloaded."
          : createAsDraft
          ? "Invoice saved as a draft in Manage Invoices."
          : send
            ? "Invoice approved and emailed to the billing contact."
            : "Invoice approved and saved in Manage Invoices."
      );
      if (navigateAfterSave) {
        navigate(RoutePaths.chamberAdmin_invoicing_manage);
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Invoice could not be saved."
      );
    }
  }, [
    activeTab,
    chamberId,
    city,
    contactEmail,
    contactName,
    contactPhone,
    createCmpInvoice,
    creditCardPaymentsReady,
    customerKind,
    customerName,
    downloadCmpInvoicePdf,
    dueDate,
    issueDate,
    lineItems,
    members,
    navigate,
    notes,
    postalCode,
    province,
    referenceNumber,
    selectedMemberId,
    sendCmpInvoicePaymentRequest,
    street,
    totalInCents,
  ]);

  const pageHeaderActions = useMemo(
    () => (
      <div className="flex w-full flex-col items-start gap-2 sm:w-auto sm:flex-shrink-0 sm:items-end">
        <div className="flex flex-nowrap items-start justify-start gap-3 sm:justify-end">
          <Button
            variant="contained"
            disabled={submitting}
            onClick={() =>
              void submitInvoice({
                createAsDraft: true,
                downloadPdf: true,
                navigateAfterSave: false,
                send: false,
              })
            }
            startIcon={
              submitting ? (
                <CircularProgress color="inherit" size={18} />
              ) : (
                <PrintOutlinedIcon />
              )
            }
            sx={headerActionSx}
          >
            Print PDF
          </Button>
          <div style={{ width: headerDecisionActionWidth }}>
            <Button
              variant="contained"
              disabled={submitting}
              onClick={() =>
                void submitInvoice({ createAsDraft: false, send: false })
              }
              sx={headerDecisionActionSx}
            >
              Approve and Save
            </Button>
            <p className="mt-1 text-[11px] leading-4 text-gray-500">
              Invoice will be saved in manage invoices and not sent. For internal use.
            </p>
          </div>
          <div style={{ width: headerDecisionActionWidth }}>
            <Button
              variant="contained"
              disabled={submitting}
              onClick={() =>
                void submitInvoice({ createAsDraft: false, send: true })
              }
              sx={headerDecisionActionSx}
            >
              Approve and Send
            </Button>
            <p className="mt-1 text-[11px] leading-4 text-gray-500">
              Invoice has been generated and will be emailed to contact below.
            </p>
          </div>
        </div>
        <div className="text-left text-[13px] font-bold text-gray-700 sm:text-right">
          <button
            type="button"
            onClick={() => setShowEmailPreview(true)}
            className="inline-flex items-center gap-1 rounded text-gray-700 underline-offset-2 hover:text-cpaGold hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cpaGold"
          >
            {/* TICK-1 (B5): eye glyph per mock (Members Preview Email precedent). */}
            <VisibilityOutlinedIcon fontSize="small" /> Preview Email
          </button>
          <div className="text-[11px] font-medium text-gray-500">
            {contactEmail
              ? `To be sent with invoice to ${contactEmail}`
              : "To be sent with invoice"}
          </div>
        </div>
      </div>
    ),
    [contactEmail, submitInvoice, submitting]
  );

  useEffect(() => {
    setPageHeaderOverride({
      actions: pageHeaderActions,
      description:
        "Create invoice, save as draft, preview invoice, print and/or send.",
      descriptionClassName: "mt-3 text-lg leading-7 text-gray-500 whitespace-pre-line",
      title: "Create Invoice",
      titleClassName: "text-[34px] font-extrabold leading-tight text-black",
      withHeaderDivider: true,
    });

    return () => setPageHeaderOverride(null);
  }, [pageHeaderActions, setPageHeaderOverride]);

  // TICK-10 (B5): the totals row and the email preview share the tax
  // dropdown's "Tax (rate%)" label shape (guarded against double-suffixing).
  const taxSummaryLabel = formatTaxSummaryLabel(
    categoryDefaults?.taxRateLabel,
    categoryDefaults?.taxRatePercentage
  );

  // POL-019 (B5): line-item errors regrouped by line id for the table.
  const lineItemErrors: Record<string, LineItemFieldErrors> = {};
  Object.entries(formErrors).forEach(([key, message]) => {
    const match = key.match(/^lineItems\.(.+)\.(description|unitPrice)$/);

    if (!match) {
      return;
    }

    const [, lineItemId, field] = match;
    lineItemErrors[lineItemId] = {
      ...lineItemErrors[lineItemId],
      [field]: message,
    };
  });

  return (
    // B5 form-system normalization (Q-034 sanctioned): page chrome via the
    // shared FormPageShell (1180px clamp, sr-only heading, alerts slot). No
    // stepper on this page per mocks; the gold action stack stays in the
    // page-header override.
    <FormPageShell
      headingId="create-invoice-page"
      heading="Create Invoice"
      contentClassName=""
      beforeMain={
        <>
          <UnsavedChangesPrompt when={isDirty} />

      <Dialog
        open={showEmailPreview}
        onClose={() => setShowEmailPreview(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <DialogTitle className="relative text-center">
          <span className="text-xl font-bold text-black">Preview Email</span>
          <IconButton
            aria-label="Close email preview"
            onClick={() => setShowEmailPreview(false)}
            sx={{
              position: "absolute",
              right: 16,
              top: 12,
              backgroundColor: "#EF1B2D",
              color: "white",
              height: 30,
              width: 30,
              "&:hover": { backgroundColor: "#D71920" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <p className="mb-4 text-[13px] text-gray-500">
            This preview reflects the email that will be sent to the billing
            contact when you approve and send this invoice.
          </p>
          <InvoiceEmailPreview
            billingContactName={contactName}
            billingEmail={contactEmail}
            billingInquiryEmail={invoiceDefaults?.billingInquiryEmail}
            billingPhone={invoiceDefaults?.billingPhone}
            chamberName={chamber?.name || "the chamber"}
            customerName={customerName}
            dueDateInput={dueDate}
            issueDateInput={issueDate}
            lineItems={lineItems}
            paymentTermsNote={invoiceDefaults?.paymentTermsNote}
            referenceNumber={referenceNumber}
            subtotalInCents={subtotalInCents}
            taxInCents={taxInCents}
            taxRateLabel={taxSummaryLabel}
            taxRegistrationNumber={invoiceDefaults?.taxRegistrationNumber}
            totalInCents={totalInCents}
          />
        </DialogContent>
      </Dialog>

          {/* POL-007 (B5): hand-rolled fixed overlay retired for the shared
              ModalShell (headerless, UnsavedChanges-style skin). Children are
              the previous markup verbatim. */}
          <ModalShell
            open={!!pendingTabSwitch}
            onClose={() => setPendingTabSwitch(null)}
            overlayClassName="bg-black/40"
            panelClassName="relative max-w-md shadow-2xl"
            bodyClassName="text-center"
            labelledById="invoice-tab-unsaved-changes-title"
          >
            <button
              type="button"
              onClick={() => setPendingTabSwitch(null)}
              className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white transition hover:bg-red-700"
              aria-label="Close unsaved changes warning"
            >
              <CloseIcon fontSize="small" />
            </button>
            <div className="mx-auto mt-2 flex h-16 w-16 items-center justify-center rounded-full bg-cpaGold text-white">
              <InfoOutlinedIcon sx={{ fontSize: 40 }} />
            </div>
            <h2
              id="invoice-tab-unsaved-changes-title"
              className="mt-5 text-lg font-bold uppercase tracking-wide text-gray-950"
            >
              UNSAVED CHANGES
            </h2>
            <p className="mt-4 text-sm leading-6 text-gray-950">
              You have unsaved changes on this page.
            </p>
            <p className="mt-2 text-sm leading-6 text-gray-950">
              If you leave now, your changes will be lost.
            </p>
            <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setPendingTabSwitch(null)}
                className="rounded-lg border border-cpaGold bg-cpaGold px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-500"
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={handleConfirmTabSwitch}
                className="rounded-lg border border-neutral-800 bg-neutral-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-neutral-900"
              >
                Leave Without Saving
              </button>
            </div>
          </ModalShell>
        </>
      }
      alerts={
        <>
          {lookupsError ? (
            <Alert severity="error" className="mb-5">
              Invoice lookups could not be loaded: {lookupsError.message}
            </Alert>
          ) : null}
          {errorMessage ? (
            <Alert
              severity="error"
              className="mb-5"
              onClose={() => setErrorMessage(null)}
            >
              {errorMessage}
            </Alert>
          ) : null}
          {lastAction ? (
            <Alert severity="success" className="mb-5">
              {lastAction}
            </Alert>
          ) : null}
        </>
      }
    >
      <nav className="flex gap-12 border-b border-gray-200" aria-label="Invoice type">
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const Icon = tab.Icon;

          return (
          <button
            key={tab.id}
            type="button"
            onClick={() => handleChangeTab(tab.id)}
            className={`relative flex min-w-[168px] flex-row items-center justify-center gap-3 border-b-[3px] px-5 pb-4 pt-2 text-center ${
              isActive
                ? "border-cpaGold text-[#C88B16]"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
            }`}
            aria-current={isActive ? "page" : undefined}
          >
            <span
              className={`flex h-[58px] w-[58px] items-center justify-center rounded-full border ${
                isActive
                  ? "border-cpaGold bg-cpaGold text-white"
                  : "border-gray-300 bg-white text-gray-500"
              }`}
            >
              <Icon sx={{ fontSize: 30 }} />
            </span>
            <span className="text-[16px] font-extrabold">{tab.label}</span>
          </button>
          );
        })}
      </nav>

      <div className="mt-8 space-y-10">
        {activeTab === "advertising" ? (
          <AdvertisingOptionsManager
            advertisingOptions={advertisingOptions}
            chamberId={chamberId}
            isCmpInvoiceModuleTestData={shouldMarkCmpInvoiceModuleTestData}
            loading={advertisingLoading}
            onChanged={() => void refetchAdvertisingOptions()}
          />
        ) : null}

        <InvoiceSection label="Member" title="Member Information" variant="plain">
          <div className="space-y-6">
            <div>
              <label className="mb-1 block text-sm font-bold text-gray-900">
                Bill To <span className="text-red-500">*</span>
              </label>
              <p className="mb-2 text-xs text-gray-500">
                Invoice an existing member or a one-off, non-member customer.
              </p>
              <div className="inline-flex rounded-md border border-gray-300 p-1">
                {(
                  [
                    { value: "member", label: "Existing member" },
                    {
                      value: "non-member",
                      label: "Non-member / one-off customer",
                    },
                  ] as const
                ).map((option) => {
                  const isActive = customerKind === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleChangeCustomerKind(option.value)}
                      aria-pressed={isActive}
                      className={`rounded px-4 py-2 text-sm font-bold transition ${
                        isActive
                          ? "bg-cpaGold text-white"
                          : "bg-transparent text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {customerKind === "member" ? (
              <div
                data-invoice-field-error={
                  getFieldError("memberId") ? "true" : undefined
                }
              >
                <label
                  className={`mb-1 block text-sm font-bold ${
                    getFieldError("memberId") ? "text-red-600" : "text-gray-900"
                  }`}
                >
                  Member <span className="text-red-500">*</span>
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Select the member from the drop down.
                </p>
                <TextField
                  select
                  fullWidth
                  sx={invoiceFieldSx}
                  value={selectedMemberId}
                  disabled={lookupsLoading && members.length === 0}
                  error={Boolean(getFieldError("memberId"))}
                  helperText={getFieldError("memberId") || undefined}
                  onChange={({ target }) => handleSelectMember(target.value)}
                  SelectProps={{
                    displayEmpty: true,
                    MenuProps: {
                      anchorOrigin: { vertical: "bottom", horizontal: "left" },
                      transformOrigin: { vertical: "top", horizontal: "left" },
                    },
                  }}
                >
                  <MenuItem value="">
                    {lookupsLoading && members.length === 0
                      ? "Loading members..."
                      : "Select Member Name"}
                  </MenuItem>
                  {members.map((member) => (
                    <MenuItem value={member.id} key={member.id}>
                      {member.label}
                      {member.description ? ` — ${member.description}` : ""}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            ) : (
              <div
                data-invoice-field-error={
                  getFieldError("customerName") ? "true" : undefined
                }
              >
                <label
                  className={`mb-1 block text-sm font-bold ${
                    getFieldError("customerName")
                      ? "text-red-600"
                      : "text-gray-900"
                  }`}
                >
                  Customer / Company Name <span className="text-red-500">*</span>
                </label>
                <p className="mb-2 text-xs text-gray-500">
                  Enter the customer or company being invoiced.
                </p>
                <TextField
                  placeholder="Enter Customer or Company Name"
                  value={customerName}
                  sx={invoiceFieldSx}
                  error={Boolean(getFieldError("customerName"))}
                  helperText={getFieldError("customerName") || undefined}
                  onChange={({ target }) => {
                    setCustomerName(target.value);
                    setIsDirty(true);
                    clearFieldError("customerName");
                  }}
                  fullWidth
                />
              </div>
            )}

            <div
              data-invoice-field-error={
                getFieldError("street") ||
                getFieldError("city") ||
                getFieldError("postalCode")
                  ? "true"
                  : undefined
              }
            >
              <label
                className={`mb-1 block text-sm font-bold ${
                  getFieldError("street") ||
                  getFieldError("city") ||
                  getFieldError("postalCode")
                    ? "text-red-600"
                    : "text-gray-900"
                }`}
              >
                Billing Address <span className="text-red-500">*</span>
              </label>
              <p className="mb-2 text-xs text-gray-500">Edit if not shown correctly.</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <TextField
                  placeholder="Enter Street Number and Name"
                  value={street}
                  sx={invoiceFieldSx}
                  error={Boolean(getFieldError("street"))}
                  helperText={getFieldError("street") || undefined}
                  onChange={({ target }) => {
                    setStreet(target.value);
                    setIsDirty(true);
                    clearFieldError("street");
                  }}
                  fullWidth
                />
                <TextField
                  placeholder="Enter City Name"
                  value={city}
                  sx={invoiceFieldSx}
                  error={Boolean(getFieldError("city"))}
                  helperText={getFieldError("city") || undefined}
                  onChange={({ target }) => {
                    setCity(target.value);
                    setIsDirty(true);
                    clearFieldError("city");
                  }}
                  fullWidth
                />
                <TextField
                  placeholder="Enter Postal Code"
                  value={postalCode}
                  sx={invoiceFieldSx}
                  error={Boolean(getFieldError("postalCode"))}
                  helperText={getFieldError("postalCode") || undefined}
                  onChange={({ target }) => {
                    setPostalCode(target.value);
                    setIsDirty(true);
                    clearFieldError("postalCode");
                  }}
                  fullWidth
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div
                data-invoice-field-error={
                  getFieldError("contactName") ? "true" : undefined
                }
              >
                <label
                  className={`mb-1 block text-sm font-bold ${
                    getFieldError("contactName")
                      ? "text-red-600"
                      : "text-gray-900"
                  }`}
                >
                  Billing Contact Name <span className="text-red-500">*</span>
                </label>
                <TextField
                  placeholder="Enter Contact Name"
                  value={contactName}
                  sx={invoiceFieldSx}
                  error={Boolean(getFieldError("contactName"))}
                  helperText={getFieldError("contactName") || undefined}
                  onChange={({ target }) => {
                    setContactName(target.value);
                    setIsDirty(true);
                    clearFieldError("contactName");
                  }}
                  fullWidth
                />
              </div>
              <div
                data-invoice-field-error={
                  getFieldError("contactEmail") ? "true" : undefined
                }
              >
                <label
                  className={`mb-1 block text-sm font-bold ${
                    getFieldError("contactEmail")
                      ? "text-red-600"
                      : "text-gray-900"
                  }`}
                >
                  Email <span className="text-red-500">*</span>
                </label>
                <TextField
                  placeholder="Enter Contact Email"
                  value={contactEmail}
                  sx={invoiceFieldSx}
                  error={Boolean(getFieldError("contactEmail"))}
                  helperText={getFieldError("contactEmail") || undefined}
                  onChange={({ target }) => {
                    setContactEmail(target.value);
                    setIsDirty(true);
                    clearFieldError("contactEmail");
                  }}
                  fullWidth
                />
              </div>
              <div>
                {/* TICK-5 (B5): mock shows "Phone *" — the asterisk is
                    visual-only per operator decision; phone stays optional
                    and unenforced (backend contactPhone is optional). */}
                <label className="mb-1 block text-sm font-bold text-gray-900">
                  Phone <span className="text-red-500">*</span>
                </label>
                <TextField
                  placeholder="Enter Phone Number"
                  value={contactPhone}
                  sx={invoiceFieldSx}
                  onChange={({ target }) => {
                    setContactPhone(target.value);
                    setIsDirty(true);
                  }}
                  fullWidth
                />
              </div>
            </div>
          </div>
        </InvoiceSection>

        <InvoiceSection label="Details" title="Invoice Details" variant="plain">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div
              data-invoice-field-error={
                getFieldError("issueDate") ? "true" : undefined
              }
            >
              <label
                className={`mb-1 block text-sm font-bold ${
                  getFieldError("issueDate") ? "text-red-600" : "text-gray-900"
                }`}
              >
                Invoice Date <span className="text-red-500">*</span>
              </label>
              <TextField
                type="date"
                value={issueDate}
                sx={invoiceFieldSx}
                error={Boolean(getFieldError("issueDate"))}
                helperText={getFieldError("issueDate") || undefined}
                onChange={({ target }) => {
                  setIssueDate(target.value);
                  setIsDirty(true);
                  clearFieldError("issueDate");
                }}
                fullWidth
              />
            </div>
            <div
              data-invoice-field-error={
                getFieldError("dueDate") ? "true" : undefined
              }
            >
              <label
                className={`mb-1 block text-sm font-bold ${
                  getFieldError("dueDate") ? "text-red-600" : "text-gray-900"
                }`}
              >
                Due Date <span className="text-red-500">*</span>
              </label>
              <TextField
                type="date"
                value={dueDate}
                sx={invoiceFieldSx}
                error={Boolean(getFieldError("dueDate"))}
                helperText={getFieldError("dueDate") || undefined}
                onChange={({ target }) => {
                  setDueDate(target.value);
                  setDueDateManuallyEdited(true);
                  setIsDirty(true);
                  clearFieldError("dueDate");
                }}
                fullWidth
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-bold text-gray-900">
                Reference / PO Number (Optional)
              </label>
              <TextField
                placeholder="PO-12345"
                value={referenceNumber}
                sx={invoiceFieldSx}
                onChange={({ target }) => {
                  setReferenceNumber(target.value);
                  setIsDirty(true);
                }}
                fullWidth
              />
            </div>
          </div>
        </InvoiceSection>

        <InvoiceSection label="Items" title="Create Invoice" variant="plain">
          <p className="mb-4 text-sm leading-6 text-gray-600">
            To generate an invoice select description options from the drop down and the
            code will be populated. Then enter quantity. Account # will populate along with
            unit price. The tax rate will be shown. To not charge tax for a line use the
            drop down and select Tax Exempt. Verify all totals.
          </p>
          <InvoiceLineItemsTable
            accountNumber={accountNumber}
            catalogOptions={lineItemCatalogOptions}
            lineItems={lineItems}
            lineErrors={lineItemErrors}
            onChange={handleLineItemsChange}
            taxOptions={taxOptions}
          />

          <div className="mt-6 flex flex-col items-end gap-2">
            <div className="w-full max-w-[320px] space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(subtotalInCents)}
                </span>
              </div>
              <div className="flex justify-between border-b border-gray-200 pb-2 text-gray-600">
                {/* TICK-10 (B5): "Tax (rate%)" shape per mock. */}
                <span>{taxSummaryLabel}</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(taxInCents)}
                </span>
              </div>
              <div className="flex items-end justify-between pt-1">
                <span className="text-base font-extrabold text-gray-900">TOTAL DUE</span>
                <span className="text-2xl font-extrabold text-gray-900">
                  {formatCurrency(totalInCents)}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <TextField
              placeholder="Add internal notes that won't appear on the invoice."
              value={notes}
              sx={invoiceFieldSx}
              onChange={({ target }) => {
                setNotes(target.value);
                setIsDirty(true);
              }}
              multiline
              minRows={3}
              fullWidth
            />
          </div>
        </InvoiceSection>

        <section className="bg-white pb-4 pt-2">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <h3 className="text-[20px] font-extrabold text-gray-900">
                Payment Terms
              </h3>
              <p className="mt-3 text-[15px] leading-7 text-gray-600">
                {invoiceDefaults?.paymentTermsNote ||
                  `Thank you for your support of ${chamber?.name || "the chamber"}.`}
              </p>
            </div>
            <div className="rounded-md border border-gray-200 p-6">
              <h3 className="text-[20px] font-extrabold text-gray-900">
                Billing inquiries contact
              </h3>
              {/* TICK-7 (B5): blue MUI email/phone icons per mock (#1684FF is
                  the mock's invoice blue, same family as the table band). */}
              {invoiceDefaults?.billingInquiryEmail ? (
                <p className="mt-3 flex items-center gap-2 text-[15px] text-gray-600">
                  <EmailIcon sx={{ color: "#1684FF", fontSize: 20 }} />
                  {invoiceDefaults.billingInquiryEmail}
                </p>
              ) : null}
              {invoiceDefaults?.billingPhone ? (
                <p className="mt-2 flex items-center gap-2 text-[15px] text-gray-600">
                  <PhoneIcon sx={{ color: "#1684FF", fontSize: 20 }} />
                  {invoiceDefaults.billingPhone}
                </p>
              ) : null}
            </div>
          </div>
          <div className="mt-8 border-t border-gray-100 pt-5 text-center text-[15px] font-bold text-gray-900">
            {chamber?.name || "Chamber of Commerce"} HST#
            {invoiceDefaults?.taxRegistrationNumber
              ? `: ${invoiceDefaults.taxRegistrationNumber}`
              : ""}
          </div>
          {/* TICK-6 (B5): centered closing line below the HST# line per mock. */}
          <div className="mt-4 text-center text-[13px] text-gray-900">
            Thank you for being a valued member of the{" "}
            {chamber?.name || "Chamber of Commerce"}.
          </div>
        </section>
      </div>
    </FormPageShell>
  );
};

export default CreateInvoice;
